<template>
  <div class="profile-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>个人信息</span>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="content-area" v-loading="loading">
      <el-card class="profile-card">
        <el-form :model="userForm" :rules="rules" ref="userForm" label-width="120px">
          <!-- 基本信息 -->
          <div class="form-section">
            <div class="section-title">基本信息</div>
            
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="用户名" prop="username">
                  <el-input v-model="userForm.username" disabled></el-input>
                  <div class="form-tip">用户名不可修改</div>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="手机号" prop="phone">
                  <el-input v-model="userForm.phone" placeholder="请输入手机号"></el-input>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="所属单位" prop="unitId">
                  <el-select 
                    v-model="userForm.unitId" 
                    placeholder="请选择单位" 
                    filterable
                    style="width: 100%"
                    @change="handleUnitChange"
                  >
                    <el-option
                      v-for="item in unitOptions"
                      :key="item.id"
                      :label="item.unitName"
                      :value="item.id">
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="角色" prop="roleId">
                  <el-select 
                    v-model="userForm.roleId" 
                    placeholder="请选择角色" 
                    style="width: 100%"
                    disabled
                  >
                    <el-option
                      v-for="role in roleOptions"
                      :key="role.id"
                      :label="role.description || role.roleName"
                      :value="role.id">
                      <span style="float: left">{{ role.description || role.roleName }}</span>
                      <span style="float: right; color: #8492a6; font-size: 13px">{{ role.roleName }}</span>
                    </el-option>
                  </el-select>
                  <div class="form-tip">角色不可修改</div>
                </el-form-item>
              </el-col>
            </el-row>

            <el-row :gutter="20" v-if="isProjectDepartmentRole">
              <el-col :span="24">
                <el-form-item label="关联项目" prop="projectIds">
                  <el-select 
                    v-model="userForm.projectIds" 
                    placeholder="请先选择单位，然后选择项目（支持多选）" 
                    multiple
                    filterable
                    style="width: 100%"
                    :disabled="!userForm.unitId"
                  >
                    <el-option
                      v-for="project in projectOptions"
                      :key="project.id"
                      :label="project.projectName"
                      :value="project.id">
                    </el-option>
                  </el-select>
                  <div v-if="!userForm.unitId" class="form-tip">
                    请先选择单位
                  </div>
                  <div v-else-if="projectOptions.length === 0" class="form-tip">
                    该单位暂无项目数据
                  </div>
                  <div v-else-if="userForm.projectIds && userForm.projectIds.length > 0" class="form-tip success">
                    已选择 {{ userForm.projectIds.length }} 个项目
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 签名图片 -->
          <div class="form-section">
            <div class="section-title">签名图片</div>
            <el-form-item label="签名图片" prop="signatureImagePath">
              <el-upload
                ref="signatureUpload"
                class="signature-uploader"
                :http-request="handleSignatureUpload"
                :before-upload="beforeFileUpload"
                :show-file-list="false"
                accept=".jpg,.jpeg,.png"
              >
                <img v-if="signaturePreview" :src="signaturePreview" class="signature-image" />
                <div v-else class="upload-placeholder">
                  <i class="el-icon-plus signature-uploader-icon"></i>
                  <div class="upload-text">点击上传签名图片</div>
                </div>
              </el-upload>
              <div class="el-upload__tip">支持jpg/png文件，且不超过10MB</div>
              <div v-if="userForm.signatureImagePath && !signaturePreview" class="file-path">
                <el-tag size="small" closable @close="clearSignature">{{ userForm.signatureImagePath }}</el-tag>
              </div>
            </el-form-item>
          </div>

          <!-- 其他信息（只读） -->
          <div class="form-section">
            <div class="section-title">其他信息</div>
            <el-row :gutter="20">
              <el-col :span="12">
                <el-form-item label="状态">
                  <el-tag :type="userForm.status === 'ACTIVE' ? 'success' : 'info'">
                    {{ userForm.status === 'ACTIVE' ? '活跃' : '已删除' }}
                  </el-tag>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="创建时间">
                  <span>{{ formatDateTime(userForm.createTime) }}</span>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 操作按钮 -->
          <div class="form-actions">
            <el-button @click="handleReset">重置</el-button>
            <el-button type="primary" @click="handleSave" :loading="saving">保存</el-button>
          </div>
        </el-form>
      </el-card>
    </div>
  </div>
</template>

<script>
import { query_user_by_id, save_user } from '@/api/modules/user'
import { getAllUnits } from '@/api/modules/unit'
import { getAllRoles } from '@/api/modules/role'
import { upload_attachment } from '@/api/modules/attachment'
import { getProjectsByUnit } from '@/api/modules/projects'
import { getUserInfo } from '@/utils/auth'
import { getApiUrl } from '@/utils/env'
import moment from 'moment'

export default {
  name: 'Profile',
  data() {
    return {
      loading: false,
      saving: false,
      userForm: {
        id: null,
        username: '',
        phone: '',
        unitId: null,
        unitName: '',
        roleId: null,
        roleName: '',
        projectIds: [],
        signatureImagePath: '',
        status: '',
        createTime: null
      },
      // 原始数据（用于重置）
      originalData: {},
      // 单位选项
      unitOptions: [],
      // 角色选项
      roleOptions: [],
      // 项目选项
      projectOptions: [],
      // 角色名称到ID的映射
      roleNameToIdMap: {},
      // 签名图片预览
      signaturePreview: '',
      // 表单验证规则
      rules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
        ],
        unitId: [
          { required: true, message: '请选择所属单位', trigger: 'change' }
        ],
        roleId: [
          { required: true, message: '请选择角色', trigger: 'change' }
        ],
        projectIds: [
          { 
            validator: (rule, value, callback) => {
              // 如果是项目部角色，必须选择至少一个项目
              if (this.isProjectDepartmentRole && (!value || value.length === 0)) {
                callback(new Error('项目部角色必须选择至少一个项目'))
              } else {
                callback()
              }
            }, 
            trigger: 'change' 
          }
        ]
      }
    }
  },
  computed: {
    // 判断当前选择的角色是否是项目部
    isProjectDepartmentRole() {
      if (!this.userForm.roleId) {
        return false
      }
      const selectedRole = this.roleOptions.find(role => role.id === this.userForm.roleId)
      if (!selectedRole) {
        return false
      }
      const roleName = selectedRole.roleName || ''
      const description = selectedRole.description || ''
      return roleName.includes('PROJECT') || 
             roleName.includes('项目') || 
             description.includes('项目')
    }
  },
  created() {
    this.loadUserData()
    this.loadRoleList()
  },
  methods: {
    // 加载用户数据
    async loadUserData() {
      const currentUserInfo = getUserInfo()
      if (!currentUserInfo || !currentUserInfo.userId) {
        this.$message.error('无法获取当前用户信息，请重新登录')
        this.$router.push('/login')
        return
      }

      this.loading = true
      try {
        // 获取完整的用户信息
        const response = await query_user_by_id(currentUserInfo.userId)
if (response.data && response.data.success) {
          const userData = response.data.data
          
          // 填充表单数据
          this.userForm = {
            id: userData.id,
            username: userData.username || '',
            phone: userData.phone || '',
            unitId: userData.unitId || null,
            unitName: userData.unitName || '',
            roleId: userData.roleId || null,
            roleName: userData.roleName || '',
            projectIds: userData.projectIds || [],
            signatureImagePath: userData.signatureImagePath || '',
            status: userData.status || '',
            createTime: userData.createTime || null
          }

          // 保存原始数据用于重置
          this.originalData = JSON.parse(JSON.stringify(this.userForm))

          // 如果有签名图片，加载预览
          if (userData.signatureImagePath) {
            this.loadSignaturePreview(userData.signatureImagePath)
          }

          // 加载单位列表
          await this.loadUnitList()

          // 如果已有单位ID，加载项目列表
          if (this.userForm.unitId && this.isProjectDepartmentRole) {
            await this.loadProjectList()
          }
        } else {
          this.$message.error(response.data?.message || '获取用户信息失败')
        }
      } catch (error) {
        console.error('获取用户信息失败:', error)
        this.$message.error('获取用户信息失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },

    // 加载角色列表
    async loadRoleList() {
      try {
        const response = await getAllRoles()
if (response.data && response.data.success) {
          this.roleOptions = response.data.data || []
          
          // 构建角色映射
          this.roleOptions.forEach(role => {
            this.roleNameToIdMap[role.roleName] = role.id
          })
        } else {
          console.error('获取角色列表失败:', response.data?.message)
        }
      } catch (error) {
        console.error('获取角色列表失败:', error)
      }
    },

    // 加载单位列表
    async loadUnitList() {
      try {
        const response = await getAllUnits()
if (response.data && response.data.success) {
          this.unitOptions = response.data.data || []
        } else {
          console.error('获取单位列表失败:', response.data?.message)
        }
      } catch (error) {
        console.error('获取单位列表失败:', error)
      }
    },

    // 加载项目列表
    async loadProjectList() {
      if (!this.userForm.unitId) {
        this.projectOptions = []
        return
      }

      try {
        const response = await getProjectsByUnit(this.userForm.unitId)
if (response.data && response.data.success) {
          this.projectOptions = response.data.data || []
        } else {
          console.error('获取项目列表失败:', response.data?.message)
          this.projectOptions = []
        }
      } catch (error) {
        console.error('获取项目列表失败:', error)
        this.projectOptions = []
      }
    },

    // 单位变化处理
    handleUnitChange(unitId) {
      // 如果是项目部角色，加载该单位的项目列表
      if (this.isProjectDepartmentRole && unitId) {
        this.userForm.projectIds = [] // 清空之前选择的项目
        this.loadProjectList()
      } else {
        this.projectOptions = []
        this.userForm.projectIds = []
      }
    },


    // 文件上传前验证
    beforeFileUpload(file) {
      const maxSize = 10 * 1024 * 1024 // 10MB
      const isImage = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg'
      
      if (!isImage) {
        this.$message.error('只能上传jpg/png格式的图片')
        return false
      }
      
      if (file.size > maxSize) {
        this.$message.error('文件大小不能超过10MB')
        return false
      }
      
      // 检查图片尺寸（Canvas处理大图片可能会导致性能问题）
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            const maxDimension = 4000 // 最大尺寸4000像素
            const maxPixels = 16000000 // 最大总像素数（4000*4000）
            
            if (img.width > maxDimension || img.height > maxDimension) {
              this.$message.error(`图片尺寸过大，宽度和高度不能超过${maxDimension}像素`)
              resolve(false)
            } else if (img.width * img.height > maxPixels) {
              this.$message.error(`图片像素数过大，总像素数不能超过${maxPixels.toLocaleString()}像素`)
              resolve(false)
            } else {
              resolve(true)
            }
          }
          img.onerror = () => {
            this.$message.error('图片格式错误，无法读取')
            resolve(false)
          }
          img.src = e.target.result
        }
        reader.onerror = () => {
          this.$message.error('文件读取失败')
          resolve(false)
        }
        reader.readAsDataURL(file)
      })
    },

    // 将图片背景处理为透明
    async processImageToTransparent(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const img = new Image()
          img.onload = () => {
            try {
              // 创建Canvas
              const canvas = document.createElement('canvas')
              canvas.width = img.width
              canvas.height = img.height
              const ctx = canvas.getContext('2d')
              
              // 绘制图片
              ctx.drawImage(img, 0, 0)
              
              // 获取图像数据
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
              const data = imageData.data
              
              // 采样边缘区域来确定背景色（不只是四个角，而是整个边缘）
              const edgeSamples = []
              const edgeWidth = Math.max(5, Math.floor(canvas.width * 0.05)) // 边缘采样宽度，至少5像素
              const edgeHeight = Math.max(5, Math.floor(canvas.height * 0.05))
              
              // 采样上边缘
              for (let x = 0; x < canvas.width; x++) {
                for (let y = 0; y < edgeHeight; y++) {
                  const index = (y * canvas.width + x) * 4
                  edgeSamples.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2]
                  })
                }
              }
              
              // 采样下边缘
              for (let x = 0; x < canvas.width; x++) {
                for (let y = canvas.height - edgeHeight; y < canvas.height; y++) {
                  const index = (y * canvas.width + x) * 4
                  edgeSamples.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2]
                  })
                }
              }
              
              // 采样左边缘
              for (let y = 0; y < canvas.height; y++) {
                for (let x = 0; x < edgeWidth; x++) {
                  const index = (y * canvas.width + x) * 4
                  edgeSamples.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2]
                  })
                }
              }
              
              // 采样右边缘
              for (let y = 0; y < canvas.height; y++) {
                for (let x = canvas.width - edgeWidth; x < canvas.width; x++) {
                  const index = (y * canvas.width + x) * 4
                  edgeSamples.push({
                    r: data[index],
                    g: data[index + 1],
                    b: data[index + 2]
                  })
                }
              }
              
              // 计算边缘颜色的平均值（背景色）
              const avgR = Math.round(edgeSamples.reduce((sum, c) => sum + c.r, 0) / edgeSamples.length)
              const avgG = Math.round(edgeSamples.reduce((sum, c) => sum + c.g, 0) / edgeSamples.length)
              const avgB = Math.round(edgeSamples.reduce((sum, c) => sum + c.b, 0) / edgeSamples.length)
              
// 计算颜色差异阈值
              // 对于接近白色的背景，使用较大的容差
              let colorTolerance = 90 // 增加初始容差值
              
              // 如果背景色接近白色（RGB值都大于200），使用更大的容差
              if (avgR > 200 && avgG > 200 && avgB > 200) {
                colorTolerance = 110 // 白色背景使用更大的容差
              }
              
              // 处理每个像素，将接近背景色的像素转换为透明
              for (let i = 0; i < data.length; i += 4) {
                const r = data[i]
                const g = data[i + 1]
                const b = data[i + 2]
                
                // 计算当前像素与背景色的欧氏距离
                const rDiff = Math.abs(r - avgR)
                const gDiff = Math.abs(g - avgG)
                const bDiff = Math.abs(b - avgB)
                
                // 计算颜色距离（欧氏距离）
                const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff)
                
                // 如果颜色距离在容差范围内，认为是背景，设置为透明
                if (colorDistance <= colorTolerance) {
                  data[i + 3] = 0 // 完全透明
                } else {
                  // 对于接近容差边界的像素，使用渐变透明，使边缘更自然
                  const fadeStart = colorTolerance * 0.8 // 从80%容差开始渐变
                  if (colorDistance <= colorTolerance * 1.3 && colorDistance > fadeStart) {
                    const fadeRatio = (colorDistance - fadeStart) / (colorTolerance * 1.3 - fadeStart)
                    data[i + 3] = Math.round(255 * fadeRatio) // 渐变透明度
                  }
                }
              }
              
              // 二次处理：对于边缘区域，如果周围大部分像素都是透明的，也将其设为透明
              // 这样可以去除一些孤立的背景像素
              const edgeCleanupPasses = 3 // 增加边缘清理的迭代次数
              for (let pass = 0; pass < edgeCleanupPasses; pass++) {
                for (let y = 1; y < canvas.height - 1; y++) {
                  for (let x = 1; x < canvas.width - 1; x++) {
                    const index = (y * canvas.width + x) * 4
                    const currentAlpha = data[index + 3]
                    
                    // 如果当前像素不是完全透明的，检查周围像素
                    if (currentAlpha > 0) {
                      let transparentNeighbors = 0
                      let totalNeighbors = 0
                      
                      // 检查8个邻居像素
                      for (let dy = -1; dy <= 1; dy++) {
                        for (let dx = -1; dx <= 1; dx++) {
                          if (dx === 0 && dy === 0) continue
                          const neighborIndex = ((y + dy) * canvas.width + (x + dx)) * 4
                          totalNeighbors++
                          if (data[neighborIndex + 3] === 0) {
                            transparentNeighbors++
                          }
                        }
                      }
                      
                      // 如果周围超过50%的像素是透明的，且当前像素接近背景色，也设为透明
                      // 降低阈值，更激进地去除背景
                      if (transparentNeighbors / totalNeighbors > 0.5) {
                        const r = data[index]
                        const g = data[index + 1]
                        const b = data[index + 2]
                        const rDiff = Math.abs(r - avgR)
                        const gDiff = Math.abs(g - avgG)
                        const bDiff = Math.abs(b - avgB)
                        const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff)
                        
                        // 增加容差范围
                        if (colorDistance <= colorTolerance * 1.5) {
                          data[index + 3] = 0
                        }
                      }
                    }
                  }
                }
              }
              
              // 第三次处理：专门处理角落区域，更激进地去除背景
              // 增加角落处理区域的大小（从10%增加到15%）
              const cornerSize = Math.max(30, Math.floor(Math.min(canvas.width, canvas.height) * 0.15))
              const corners = [
                { x: 0, y: 0, width: cornerSize, height: cornerSize }, // 左上
                { x: canvas.width - cornerSize, y: 0, width: cornerSize, height: cornerSize }, // 右上
                { x: 0, y: canvas.height - cornerSize, width: cornerSize, height: cornerSize }, // 左下
                { x: canvas.width - cornerSize, y: canvas.height - cornerSize, width: cornerSize, height: cornerSize } // 右下
              ]
              
              corners.forEach(corner => {
                for (let y = corner.y; y < corner.y + corner.height; y++) {
                  for (let x = corner.x; x < corner.x + corner.width; x++) {
                    const index = (y * canvas.width + x) * 4
                    const r = data[index]
                    const g = data[index + 1]
                    const b = data[index + 2]
                    const rDiff = Math.abs(r - avgR)
                    const gDiff = Math.abs(g - avgG)
                    const bDiff = Math.abs(b - avgB)
                    const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff)
                    
                    // 在角落区域使用更大的容差（增加到2.0倍）
                    if (colorDistance <= colorTolerance * 2.0) {
                      data[index + 3] = 0
                    }
                  }
                }
              })
              
              // 第四次处理：专门处理底部边缘区域（可能的小三角残留）
              const bottomEdgeHeight = Math.max(40, Math.floor(canvas.height * 0.12))
              for (let y = canvas.height - bottomEdgeHeight; y < canvas.height; y++) {
                for (let x = 0; x < canvas.width; x++) {
                  const index = (y * canvas.width + x) * 4
                  const currentAlpha = data[index + 3]
                  
                  // 如果当前像素不是完全透明的，检查是否接近背景色
                  if (currentAlpha > 0) {
                    const r = data[index]
                    const g = data[index + 1]
                    const b = data[index + 2]
                    const rDiff = Math.abs(r - avgR)
                    const gDiff = Math.abs(g - avgG)
                    const bDiff = Math.abs(b - avgB)
                    const colorDistance = Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff)
                    
                    // 在底部边缘使用更大的容差
                    if (colorDistance <= colorTolerance * 2.2) {
                      data[index + 3] = 0
                    }
                  }
                }
              }
              
// 将处理后的数据写回Canvas
              ctx.putImageData(imageData, 0, 0)
              
              // 将Canvas转换为Blob
              canvas.toBlob((blob) => {
                if (blob) {
                  resolve(blob)
                } else {
                  reject(new Error('图片处理失败'))
                }
              }, 'image/png') // PNG格式支持透明
            } catch (error) {
              reject(error)
            }
          }
          img.onerror = () => reject(new Error('图片加载失败'))
          img.src = e.target.result
        }
        reader.onerror = () => reject(new Error('文件读取失败'))
        reader.readAsDataURL(file)
      })
    },

    // 上传签名图片
    async handleSignatureUpload(options) {
      const file = options.file
      
      try {
        // 使用用户ID作为relatedId
        const relatedId = this.userForm.id || 0
        
        // 处理图片：将背景转换为透明
        let processedFile = file
        try {
          this.$message.info('正在处理图片...')
          processedFile = await this.processImageToTransparent(file)
          
          // 如果是图片，生成预览（使用处理后的图片）
          if (processedFile.type.startsWith('image/')) {
            const reader = new FileReader()
            reader.onload = (e) => {
              this.signaturePreview = e.target.result
            }
            reader.readAsDataURL(processedFile)
          }
        } catch (error) {
          console.warn('图片处理失败，使用原图:', error)
          // 如果处理失败，使用原图并生成预览
          if (file.type.startsWith('image/')) {
            const reader = new FileReader()
            reader.onload = (e) => {
              this.signaturePreview = e.target.result
            }
            reader.readAsDataURL(file)
          }
        }
        
        // 使用通用附件上传API
        const formData = new FormData()
        formData.append('file', processedFile, file.name || 'signature.png')
        formData.append('category', 'user-signature')
        formData.append('relatedId', relatedId)
        
        const response = await upload_attachment(formData)
        
        if (response.data && response.data.success) {
          this.userForm.signatureImagePath = response.data.data?.filePath || ''
          this.$message.success('签名图片上传成功')
          options.onSuccess(response.data)
        } else {
          this.$message.error(response.data?.message || '上传失败')
          options.onError(new Error(response.data?.message || '上传失败'))
        }
      } catch (error) {
        console.error('上传签名图片失败:', error)
        this.$message.error('上传失败: ' + (error.response?.data?.message || error.message || '未知错误'))
        options.onError(error)
      }
    },

    // 加载签名图片预览
    loadSignaturePreview(filePath) {
      if (!filePath) {
        this.signaturePreview = ''
        return
      }
      
      // 如果是完整的URL（http/https开头），直接使用
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
        this.signaturePreview = filePath
        return
      }
      
      // 如果是data URL（base64），直接使用
      if (filePath.startsWith('data:')) {
        this.signaturePreview = filePath
        return
      }
      
      // 否则，使用附件下载接口
      const apiUrl = getApiUrl('/attachments/download')
      this.signaturePreview = `${apiUrl}?filePath=${encodeURIComponent(filePath)}`
    },

    // 清除签名图片
    clearSignature() {
      this.userForm.signatureImagePath = ''
      this.signaturePreview = ''
    },

    // 格式化日期时间
    formatDateTime(date) {
      return date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : '-'
    },

    // 重置表单
    handleReset() {
      this.$confirm('确定要重置所有修改吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 恢复原始数据
        this.userForm = JSON.parse(JSON.stringify(this.originalData))
        
        // 恢复签名预览
        if (this.userForm.signatureImagePath) {
          this.loadSignaturePreview(this.userForm.signatureImagePath)
        } else {
          this.signaturePreview = ''
        }

        // 如果单位或角色变化，需要重新加载项目列表
        if (this.userForm.unitId && this.isProjectDepartmentRole) {
          this.loadProjectList()
        } else {
          this.projectOptions = []
        }

        this.$message.success('已重置')
      }).catch(() => {})
    },

    // 保存
    async handleSave() {
      try {
        await this.$refs.userForm.validate()
        
        this.saving = true
        
        // 准备提交的数据
        const submitData = {
          id: this.userForm.id,
          username: this.userForm.username,
          phone: this.userForm.phone,
          unitId: this.userForm.unitId,
          roleId: this.userForm.roleId
        }
        
        // 如果有签名图片路径，添加签名图片字段
        if (this.userForm.signatureImagePath) {
          submitData.signatureImagePath = this.userForm.signatureImagePath
        }
        
        // 如果是项目部角色，添加项目ID列表
        if (this.isProjectDepartmentRole && this.userForm.projectIds && this.userForm.projectIds.length > 0) {
          submitData.projectIds = this.userForm.projectIds
        }
        
// 使用save_user保存用户信息（包含id时是更新）
        const response = await save_user(submitData)
if (response.data && response.data.success) {
          this.$message.success(response.data.message || '保存成功')
          
          // 更新原始数据
          this.originalData = JSON.parse(JSON.stringify(this.userForm))
          
          // 更新localStorage中的用户信息（如果单位或角色有变化）
          const currentUserInfo = getUserInfo()
          if (currentUserInfo) {
            const updatedUserInfo = {
              ...currentUserInfo,
              unitId: response.data.data?.unitId || currentUserInfo.unitId,
              unitName: response.data.data?.unitName || currentUserInfo.unitName,
              roleType: response.data.data?.roleName || currentUserInfo.roleType,
              signatureImagePath: response.data.data?.signatureImagePath || this.userForm.signatureImagePath || currentUserInfo.signatureImagePath || ''
            }
            const { setUserInfo } = require('@/utils/auth')
            setUserInfo(updatedUserInfo)
            this.$store.dispatch('setUserInfo', updatedUserInfo)
          }
        } else {
          this.$message.error(response.data?.message || '保存失败')
        }
      } catch (error) {
        if (error.message && !error.message.includes('验证')) {
          console.error('保存用户信息失败:', error)
          this.$message.error('保存失败: ' + (error.message || '未知错误'))
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-container {
  padding: 20px;
  min-height: calc(100vh - 120px);
  
  .page-header {
    margin-bottom: 20px;
    
    .header-title {
      display: flex;
      align-items: center;
      font-size: 18px;
      font-weight: bold;
      
      .title-mark {
        width: 4px;
        height: 18px;
        background: #409EFF;
        margin-right: 10px;
      }
    }
  }
  
  .content-area {
    .profile-card {
      border-radius: 4px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
      
      .form-section {
        margin-bottom: 30px;
        padding-bottom: 20px;
        border-bottom: 1px solid #f0f0f0;
        
        &:last-child {
          border-bottom: none;
        }
        
        .section-title {
          font-size: 16px;
          font-weight: 500;
          color: #333;
          margin-bottom: 20px;
          padding-left: 10px;
          border-left: 3px solid #409EFF;
        }
      }
      
      .form-tip {
        font-size: 12px;
        color: #909399;
        margin-top: 5px;
        
        &.success {
          color: #67C23A;
        }
      }
      
      .form-actions {
        margin-top: 30px;
        text-align: center;
        padding-top: 20px;
        border-top: 1px solid #f0f0f0;
      }
    }
  }
  
  // 签名图片上传样式
  .signature-uploader {
    :deep(.el-upload) {
      border: 1px dashed #d9d9d9;
      border-radius: 6px;
      cursor: pointer;
      position: relative;
      overflow: hidden;
      transition: border-color 0.3s;
      width: 178px;
      
      &:hover {
        border-color: #409EFF;
      }
    }
    
    .signature-image {
      width: 178px;
      height: 178px;
      display: block;
      object-fit: contain;
    }
    
    .upload-placeholder {
      width: 178px;
      height: 178px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      background-color: #fafafa;
      
      .signature-uploader-icon {
        font-size: 28px;
        color: #8c939d;
        margin-bottom: 8px;
      }
      
      .upload-text {
        font-size: 12px;
        color: #8c939d;
      }
    }
    
    .el-upload__tip {
      color: #606266;
      font-size: 12px;
      margin-top: 7px;
    }
    
    .file-path {
      margin-top: 10px;
    }
  }
}
</style>
